import {
  DashOutlined,
  MinusOutlined,
  SmallDashOutlined,
} from '@ant-design/icons';
// ANT Design
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
// Common
import Line from './Line';
import './Line.css';
import { NavbarInputNumber } from '../Layout/NavbarInputNumber';
import { NavbarButton } from '../Layout/NavbarButton';
import BoxActionsPanel from '../common/BoxActionsPanel';
import LabelledRow from '../common/LabelledRow';
import BoxFormControlsPortal from '../RightSide/BoxFormControlsPortal';
import { BackgroundColorPicker } from '../Layout/BackgroundColorPicker';
import ShapeKindSelector from '../Shape/ShapeKindSelector';

// Store selector
const selector =
  (boxId) =>
    ({ removeBox, updateBox, configuration, selectedBoxId }) => ({
      removeBox,
      updateBox,
      colors: configuration.colors,
      isSelected: boxId === selectedBoxId,
    });

export default React.memo(({ box }) => {
  const { t } = useTranslation();
  const { isSelected, updateBox } = useStore(selector(box.id));

  useEffect(() => {
    updateBox(box.id, (box) => {
      if (box.height > 20 && box.width <= 20) {
        const { width: w, height: h } = box;
        box.width = h;
        box.height = w;
        box.left = box.left + (w - h) / 2;
        box.top = box.top + (h - w) / 2;
        if (box.rotation) {
          box.rotation += 90;
        } else {
          box.rotation = 90;
        }
      }
      if (box.content && box.content.height) {
        box.lineHeight = box.content.height;
        box.color = box.content.color;
        box.style = box.content.style;
        box.side = box.content.side;
        delete box.content;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div
        style={{
          width: box.width,
          height: box.height,
          overflow: 'hidden',
        }}>
        {/* Repli à 10px et non à l'épaisseur par défaut : une ligne sans
            `lineHeight` vient d'un document d'avant ce champ, où le trait
            faisait 10px. */}
        <Line
          color={box.color}
          lineHeight={box.lineHeight || 10}
          style={box.style}
        />
      </div>
      {isSelected && (
        <>
          {/* Une ligne par réglage, libellé à gauche, comme le panneau des
              formes : la pastille de couleur et les trois traits alignés sans
              un mot ne disaient pas lequel réglait quoi, et le champ « 5px »
              n'annonçait pas l'épaisseur. Le BorderManager n'est plus monté :
              ses deux boutons sont masqués pour une box `line` (cf.
              borderColorDisabled / selectedBoxIsShape).
              Section « Forme » et non « Ligne » : une ligne est une variante de
              l'élément Forme, et son sélecteur de type permet d'en ressortir. */}
          <BoxFormControlsPortal title={t('shape')}>
            <ShapeKindSelector box={box} />
            <LabelledRow label={t('line-color')}>
              <BackgroundColorPicker />
            </LabelledRow>
            <LineComponent selectedBox={box} />
          </BoxFormControlsPortal>
          <BoxActionsPanel />
        </>
      )}
    </>
  );
});

const LineComponent = ({ selectedBox }) => {
  const { t } = useTranslation();
  const { updateBox } = useStore(selector(selectedBox?.id));

  const lineButtons = selectedBox?.type === 'line';
  // `style` n'est posé qu'au premier changement : une ligne neuve est pleine.
  const currentStyle = selectedBox?.style || 'solid';

  const setStyle = (style) => () => {
    updateBox(selectedBox.id, (selectedBox) => {
      selectedBox.style = style;
    });
  };

  const styleButtons = [
    { style: 'solid', label: t('solid'), icon: <MinusOutlined /> },
    { style: 'dashed', label: t('dashed'), icon: <DashOutlined /> },
    { style: 'dotted', label: t('dotted'), icon: <SmallDashOutlined /> },
  ];

  return (
    <>
      <LabelledRow label={t('line-style')}>
        {styleButtons.map(({ style, label, icon }) => (
          <NavbarButton
            key={style}
            tooltipContent={label}
            onClick={setStyle(style)}
            disabled={!lineButtons}
            // Aucun des trois traits ne montrait le style en cours : on
            // ne pouvait pas lire l'état de la ligne depuis le panneau.
            active={lineButtons && currentStyle === style}
            icon={icon}
          />
        ))}
      </LabelledRow>
      <LabelledRow label={t('line-stroke')}>
        <NavbarInputNumber
          min={1}
          max={20}
          disabled={!lineButtons}
          tooltipContent={t('line-stroke')}
          value={selectedBox?.lineHeight}
          suffix='px'
          onChange={(v) =>
            updateBox(selectedBox.id, (selectedBox) => {
              selectedBox.lineHeight = v;
              selectedBox.height = v;
            })
          }
        />
      </LabelledRow>
    </>
  );
};
